import React, { useState, useEffect } from "react";
import {
  Link,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Icon } from "@iconify/react";
import { useAuth } from "../../contexts/AuthContext";
import ProtectedRoute from "../../components/ProtectedRoute";
import { ROLES } from "../../config/authorizationConfig";

import AutomationMain from "./AutomationMain";
import AutomationExplore from "./AutomationExplore";

import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import "../../styles/InnerMenu.scss";
import "./AutomationAdmin.scss";

const VIEW_OPTIONS = [
  { value: "gallery", label: "Gallery", icon: "mdi:view-grid-outline" },
  {
    value: "comfortable",
    label: "Comfortable",
    icon: "mdi:view-module-outline",
  },
  { value: "compact", label: "Compact", icon: "mdi:view-list-outline" },
];
const VIEW_STORAGE_KEY = "automation-explore-view";

const AutomationAdmin = () => {
  const { isLoading, hasAuthority } = useAuth();
  const [visibleSubmenu, setVisibleSubmenu] = useState(null);
  const [displayMode, setDisplayMode] = useState(() => {
    try {
      const savedView = window.localStorage.getItem(VIEW_STORAGE_KEY);
      return VIEW_OPTIONS.some((view) => view.value === savedView)
        ? savedView
        : "gallery";
    } catch {
      return "gallery";
    }
  });
  const location = useLocation();
  const navigate = useNavigate();

  // Role gate only — matches the AdminDashboard tile's `hasRole("ROLE_ADMIN")`
  // check. Per-module access (NOTIFICATION/AUTOMATION) is enforced further
  // down inside AutomationMain, which shows individual modules as locked
  // rather than hiding the whole page/menu.
  const hasAutomationAccess = hasAuthority(ROLES.ADMIN);
  const menuItems = [
    {
      name: "My Automation",
      routes: ["/admin/automation/main"],
      auth: {
        customCheck: () => hasAutomationAccess,
      },
    },
    {
      name: "Explore",
      routes: ["/admin/automation/explore"],
      auth: {
        customCheck: () => hasAutomationAccess,
      },
    },
  ];

  // Filter menu items based on custom auth checks
  const filteredMenuItems = menuItems.filter(
    (item) => !item.auth.customCheck || item.auth.customCheck(),
  );

  useEffect(() => {
    const activeIndex = filteredMenuItems.findIndex((item) =>
      item.routes.some((route) => location.pathname.startsWith(route)),
    );

    if (activeIndex !== -1) setVisibleSubmenu(activeIndex);
  }, [location.pathname, filteredMenuItems]);

  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, displayMode);
    } catch {
      // Storage may be unavailable in a locked-down browser session.
    }
  }, [displayMode]);

  const handleMenuClick = (index) => {
    const item = filteredMenuItems[index];
    const isAlreadyOpen = visibleSubmenu === index;

    setVisibleSubmenu(isAlreadyOpen ? null : index);

    if (item.routes && item.routes.length > 0 && !isAlreadyOpen) {
      navigate(item.routes[0]);
    }
  };

  const getDefaultRedirect = () => {
    if (filteredMenuItems.length > 0) {
      const firstItem = filteredMenuItems[0];
      return firstItem.routes[0];
    }
    return "/admin/automation";
  };

  if (isLoading) return <LoadingScreen />;

  return (
    <div className="custom-ess-container automation-shell">
      {/* VIEW SWITCHER */}
      <div className="automation-shell__topbar">
        <div className="view-switcher" aria-label="Automation view">
          {filteredMenuItems.map((item, index) => {
            const isActive =
              visibleSubmenu === index ||
              item.routes.some((route) => location.pathname.startsWith(route));
            const isExplore = item.name === "Explore";

            return (
              <Link
                key={item.name}
                to={item.routes[0]}
                className={`view-switcher__item ${
                  isExplore ? "view-switcher__item--explore" : ""
                } ${isActive ? "view-switcher__item--active" : ""}`}
                onClick={() => handleMenuClick(index)}
                aria-current={isActive ? "page" : undefined}
              >
                {isExplore && <Icon icon="mdi:creation" />}
                {item.name}
              </Link>
            );
          })}
        </div>
        {(location.pathname.startsWith("/admin/automation/explore") ||
          location.pathname.startsWith("/admin/automation/main")) && (
          <div
            className="marketplace-view-switcher"
            aria-label="Template display"
          >
            <span>View</span>
            {VIEW_OPTIONS.map((view) => (
              <button
                key={view.value}
                type="button"
                className={displayMode === view.value ? "active" : ""}
                onClick={() => setDisplayMode(view.value)}
              >
                <Icon icon={view.icon} /> {view.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* MAIN CONTENT ROUTES */}
      <div className="custom-content">
        <Routes>
          <Route
            path="/main"
            element={
              <ProtectedRoute customCheck={() => hasAutomationAccess}>
                <AutomationMain displayMode={displayMode} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/explore"
            element={
              <ProtectedRoute customCheck={() => hasAutomationAccess}>
                <AutomationExplore displayMode={displayMode} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/"
            element={<Navigate to={getDefaultRedirect()} replace />}
          />
        </Routes>
      </div>
    </div>
  );
};

export default AutomationAdmin;
